import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Wand2, Layout, BarChart3, Search, Download, Save, Trash2,
  Play, Loader2, ChevronRight, Calendar, Database, Filter, SortAsc,
  AlertCircle, CheckCircle2, Sparkles, BookOpen, Users, Building2,
  Truck, DollarSign, RefreshCw, X, Plus, Info, Send, FileDown,
  FolderOpen, Clock, ArrowLeft
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ── Template category config — ONE color per category, not per template ───
const CAT_CONFIG = {
  'Payroll':           { border: 'border-l-emerald-500', bg: 'bg-emerald-500', text: 'text-emerald-600', light: 'bg-emerald-50', ring: 'ring-emerald-200' },
  'Accounts Payable':  { border: 'border-l-violet-500',  bg: 'bg-violet-500',  text: 'text-violet-600',  light: 'bg-violet-50',  ring: 'ring-violet-200' },
  'General Ledger':    { border: 'border-l-sky-500',     bg: 'bg-sky-500',     text: 'text-sky-600',     light: 'bg-sky-50',     ring: 'ring-sky-200' },
  'Cost Accounting':   { border: 'border-l-rose-500',    bg: 'bg-rose-500',    text: 'text-rose-600',    light: 'bg-rose-50',    ring: 'ring-rose-200' },
  'Equipment':         { border: 'border-l-amber-500',   bg: 'bg-amber-500',   text: 'text-amber-600',   light: 'bg-amber-50',   ring: 'ring-amber-200' },
};

const TEMPLATE_ICONS = {
  users: Users, calendar: Calendar, building: Building2, book: BookOpen,
  user: Users, dollar: DollarSign, target: BarChart3, truck: Truck,
};

const DATA_SOURCE_OPTS = [
  { value: 'gl', label: 'General Ledger', description: 'Checks, deposits, bills, accounts' },
  { value: 'payroll', label: 'Payroll', description: 'Pay runs, employees, YTD earnings' },
  { value: 'cost_accounting', label: 'Cost Accounting', description: 'Enterprise budget vs actual' },
  { value: 'vendors', label: 'Vendors', description: 'Vendor payment history' },
  { value: 'equipment', label: 'Equipment', description: 'Equipment inventory & costs' },
  { value: 'all', label: 'All Sources', description: 'Combine all data sources' },
];

const GROUP_BY_OPTS = [
  { value: '', label: 'No Grouping' },
  { value: 'month', label: 'Month' },
  { value: 'account', label: 'GL Account' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'employee', label: 'Employee' },
  { value: 'enterprise', label: 'Enterprise / Cost Center' },
  { value: 'period', label: 'Pay Period' },
  { value: 'type', label: 'Type / Category' },
];

const ACCENT_COLORS = [
  { label: 'Sky Blue', value: '#0ea5e9' },
  { label: 'Emerald', value: '#10b981' },
  { label: 'Violet', value: '#7c3aed' },
  { label: 'Amber', value: '#f59e0b' },
  { label: 'Rose', value: '#f43f5e' },
  { label: 'Slate', value: '#475569' },
];

function fmt(val, key) {
  if (val == null || val === '') return '—';
  if (typeof val === 'number') {
    if (['amount','pay','cost','tax','flow','budget','actual','variance','value','inflow','outflow','total','gross','net','debit','credit','ytd'].some(k => key?.toLowerCase().includes(k))) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(val);
    }
    if (key?.toLowerCase().includes('pct') || key?.toLowerCase().includes('percent')) return `${val.toFixed(1)}%`;
    return val.toLocaleString();
  }
  return String(val);
}

// ── Report Result Table ────────────────────────────────────
function ReportTable({ rows, reportName }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="text-center py-12 text-gray-400" data-testid="report-empty">
        <BarChart3 className="w-10 h-10 mx-auto mb-2 opacity-40" />
        <p className="text-sm">No data returned for this report. Try a broader date range or different filters.</p>
      </div>
    );
  }
  const headers = Object.keys(rows[0]);
  return (
    <div className="overflow-x-auto" data-testid="report-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-700 text-white">
            {headers.map(h => (
              <th key={h} className="px-3 py-2.5 text-left text-xs font-semibold tracking-wide whitespace-nowrap">
                {h.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className={`border-b border-gray-100 hover:bg-sky-50/40 transition-colors ${i % 2 === 1 ? 'bg-gray-50/60' : ''}`}>
              {headers.map(h => {
                const val = row[h];
                const isNeg = typeof val === 'number' && val < 0;
                return (
                  <td key={h} className={`px-3 py-2 text-xs ${isNeg ? 'text-red-600 font-medium' : 'text-gray-700'}`}>
                    {fmt(val, h)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="px-3 py-2 bg-gray-50 text-xs text-gray-400 border-t border-gray-100">
        {rows.length} row{rows.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
}

// ── Export & Save Toolbar ─────────────────────────────────
function ExportBar({ config, rows, onSave, saving }) {
  const [exporting, setExporting] = useState(null);

  const handleExport = async (type) => {
    setExporting(type);
    try {
      const res = await fetch(`${API_URL}/api/custom-reports/export/${type}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      });
      if (!res.ok) throw new Error(`Export failed: ${res.statusText}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${config.name.replace(/\s+/g, '_')}.${type === 'pdf' ? 'pdf' : 'csv'}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${type.toUpperCase()} exported`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="export-bar">
      <Button size="sm" variant="outline" onClick={() => handleExport('pdf')} disabled={!!exporting || !rows?.length} data-testid="export-pdf-btn">
        {exporting === 'pdf' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FileDown className="w-4 h-4 mr-1" />}
        Export PDF
      </Button>
      <Button size="sm" variant="outline" onClick={() => handleExport('csv')} disabled={!!exporting || !rows?.length} data-testid="export-csv-btn">
        {exporting === 'csv' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}
        Export CSV
      </Button>
      <Button size="sm" variant="outline" onClick={onSave} disabled={saving || !rows?.length} data-testid="save-report-btn">
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
        Save Report
      </Button>
    </div>
  );
}

// ── Template Gallery ──────────────────────────────────────
function TemplateGallery({ templates, onSelect }) {
  const categories = [...new Set(templates.map(t => t.category))];
  const [active, setActive] = useState('All');

  const filtered = active === 'All' ? templates : templates.filter(t => t.category === active);
  const groups = active === 'All' ? categories : [active];

  return (
    <div className="space-y-6" data-testid="template-gallery">

      {/* Category filter tabs */}
      <div className="flex flex-wrap gap-2">
        {['All', ...categories].map(cat => {
          const c = CAT_CONFIG[cat];
          const isActive = active === cat;
          return (
            <button
              key={cat}
              onClick={() => setActive(cat)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border ${
                isActive
                  ? 'bg-slate-800 text-white border-slate-800'
                  : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400 hover:text-slate-700'
              }`}
              data-testid={`filter-${cat.replace(/\s/g, '-')}`}
            >
              {cat} {cat !== 'All' && <span className={`ml-1 opacity-60`}>
                ({templates.filter(t => t.category === cat).length})
              </span>}
            </button>
          );
        })}
      </div>

      {/* Per-category card grids */}
      {groups.map(cat => {
        const c = CAT_CONFIG[cat] || CAT_CONFIG['General Ledger'];
        const catTemplates = filtered.filter(t => t.category === cat);
        if (!catTemplates.length) return null;
        return (
          <div key={cat}>
            {/* Category header */}
            <div className="flex items-center gap-2 mb-3">
              <span className={`inline-block w-1 h-4 rounded-full ${c.bg}`} />
              <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">{cat}</span>
              <span className="text-xs text-slate-300 ml-1">({catTemplates.length})</span>
            </div>

            {/* Grid — same color scheme for all cards in this category */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {catTemplates.map(tpl => {
                const Icon = TEMPLATE_ICONS[tpl.icon] || FileText;
                return (
                  <button
                    key={tpl.id}
                    onClick={() => onSelect(tpl)}
                    className={`group text-left bg-white rounded-xl p-4 border-l-4 ${c.border} 
                                border border-slate-100 hover:shadow-md hover:-translate-y-0.5 
                                transition-all duration-200`}
                    data-testid={`template-${tpl.id}`}
                  >
                    {/* Icon */}
                    <div className={`w-9 h-9 rounded-lg ${c.light} flex items-center justify-center mb-3`}>
                      <Icon className={`w-4.5 h-4.5 ${c.text}`} style={{ width: '18px', height: '18px' }} />
                    </div>

                    {/* Title */}
                    <p className="font-semibold text-slate-800 text-sm leading-snug mb-1">
                      {tpl.name}
                    </p>

                    {/* Description */}
                    <p className="text-xs text-slate-400 leading-relaxed line-clamp-2 mb-3">
                      {tpl.description}
                    </p>

                    {/* Footer */}
                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-medium ${c.text} opacity-70`}>{tpl.report_type}</span>
                      <ChevronRight className={`w-4 h-4 text-slate-200 group-hover:${c.text} group-hover:translate-x-0.5 transition-all`} />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Visual Builder ────────────────────────────────────────
function VisualBuilder({ initialConfig, onRun, running }) {
  const today = new Date().toISOString().slice(0, 10);
  const firstOfYear = `${new Date().getFullYear()}-01-01`;

  const [config, setConfig] = useState({
    name: initialConfig?.name || 'My Custom Report',
    description: initialConfig?.description || '',
    data_source: initialConfig?.data_source || 'gl',
    report_type: initialConfig?.report_type || 'summary',
    date_from: initialConfig?.date_from || firstOfYear,
    date_to: initialConfig?.date_to || today,
    groupBy: initialConfig?.groupBy || '',
    sort_by: initialConfig?.sort_by || 'amount',
    sort_dir: initialConfig?.sort_dir || 'desc',
    branding: initialConfig?.branding || { accent_color: '#0ea5e9' },
  });

  const set = (key, val) => setConfig(prev => ({ ...prev, [key]: val }));
  const setBranding = (key, val) => setConfig(prev => ({ ...prev, branding: { ...prev.branding, [key]: val } }));

  return (
    <div className="space-y-5" data-testid="visual-builder">
      {/* Name */}
      <div>
        <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wide">Report Name</label>
        <Input value={config.name} onChange={e => set('name', e.target.value)} placeholder="My Custom Report" data-testid="report-name-input" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Data Source */}
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Data Source</label>
          <div className="space-y-1.5">
            {DATA_SOURCE_OPTS.map(opt => (
              <button
                key={opt.value}
                onClick={() => set('data_source', opt.value)}
                className={`w-full text-left px-3 py-2 rounded-lg border text-sm transition-all ${
                  config.data_source === opt.value
                    ? 'bg-sky-50 border-sky-400 text-sky-800'
                    : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
                data-testid={`datasource-${opt.value}`}
              >
                <span className="font-medium">{opt.label}</span>
                <span className="text-xs text-gray-400 ml-2">{opt.description}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Right column: Type, Date, GroupBy, Branding */}
        <div className="space-y-4">
          {/* Report Type */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Report Type</label>
            <div className="flex gap-2">
              {['summary', 'detail', 'comparison'].map(t => (
                <button
                  key={t}
                  onClick={() => set('report_type', t)}
                  className={`flex-1 py-2 rounded-lg border text-sm font-medium capitalize transition-all ${
                    config.report_type === t ? 'bg-slate-700 text-white border-slate-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                  data-testid={`report-type-${t}`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* Date Range */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Date Range</label>
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="text-xs text-gray-400 mb-0.5 block">From</label>
                <Input type="date" value={config.date_from} onChange={e => set('date_from', e.target.value)} data-testid="date-from" />
              </div>
              <div className="flex-1">
                <label className="text-xs text-gray-400 mb-0.5 block">To</label>
                <Input type="date" value={config.date_to} onChange={e => set('date_to', e.target.value)} data-testid="date-to" />
              </div>
            </div>
          </div>

          {/* Group By */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Group By</label>
            <select
              value={config.groupBy}
              onChange={e => set('groupBy', e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-sky-500"
              data-testid="group-by"
            >
              {GROUP_BY_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          {/* Sort */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Sort</label>
            <div className="flex gap-2">
              <Input value={config.sort_by} onChange={e => set('sort_by', e.target.value)} placeholder="amount" className="flex-1 text-sm" data-testid="sort-by" />
              <select
                value={config.sort_dir}
                onChange={e => set('sort_dir', e.target.value)}
                className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-sky-500"
                data-testid="sort-dir"
              >
                <option value="desc">Desc</option>
                <option value="asc">Asc</option>
              </select>
            </div>
          </div>

          {/* PDF Accent Color */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">PDF Accent Color</label>
            <div className="flex gap-2 flex-wrap">
              {ACCENT_COLORS.map(ac => (
                <button
                  key={ac.value}
                  title={ac.label}
                  onClick={() => setBranding('accent_color', ac.value)}
                  className={`w-7 h-7 rounded-full border-2 transition-all ${config.branding?.accent_color === ac.value ? 'border-gray-800 scale-110' : 'border-transparent hover:scale-105'}`}
                  style={{ backgroundColor: ac.value }}
                  data-testid={`color-${ac.label.replace(' ', '-')}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <Button
        onClick={() => onRun(config)}
        disabled={running}
        className="w-full bg-slate-700 hover:bg-slate-800 text-white py-3 text-base font-semibold"
        data-testid="run-report-btn"
      >
        {running ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Play className="w-5 h-5 mr-2" />}
        Run Report
      </Button>
    </div>
  );
}

// ── AI Query Interface ────────────────────────────────────
function AIQueryPanel({ onResult }) {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const bottomRef = useRef(null);

  const SUGGESTIONS = [
    'Labor costs by enterprise Q1 2026',
    'All vendor payments this year',
    'Monthly cash flow 2026',
    'Employee YTD earnings',
    'Equipment inventory & costs',
  ];

  const handleQuery = async (q) => {
    const text = q || query;
    if (!text.trim()) return;
    setLoading(true);
    setHistory(h => [...h, { role: 'user', content: text }]);
    setQuery('');
    try {
      const res = await fetch(`${API_URL}/api/custom-reports/ai-query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: text }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'AI query failed');
      }
      const data = await res.json();
      setHistory(h => [...h, { role: 'assistant', data }]);
      onResult(data);
    } catch (e) {
      setHistory(h => [...h, { role: 'error', content: e.message }]);
      toast.error(e.message);
    } finally {
      setLoading(false);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  };

  return (
    <div className="flex flex-col gap-4 h-full" data-testid="ai-query-panel">

      {/* Chat window */}
      <div className="bg-slate-900 rounded-xl overflow-hidden flex flex-col" style={{ minHeight: '320px' }}>

        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-700/60">
          <div className="w-6 h-6 rounded-full bg-sky-500/20 flex items-center justify-center">
            <Sparkles className="w-3.5 h-3.5 text-sky-400" />
          </div>
          <span className="text-xs font-semibold text-sky-300 tracking-widest uppercase">AI Report Assistant</span>
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse ml-auto" />
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ maxHeight: '260px' }}>
          {history.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full py-8 space-y-2 text-center">
              <Sparkles className="w-8 h-8 text-slate-600" />
              <p className="text-slate-400 text-sm">Describe a report in plain English</p>
              <p className="text-slate-600 text-xs">"Show payroll costs by enterprise for Q1"</p>
            </div>
          )}
          {history.map((m, i) => {
            if (m.role === 'user') return (
              <div key={i} className="flex justify-end">
                <div className="max-w-[80%] bg-sky-600 text-white text-sm rounded-2xl rounded-tr-sm px-3.5 py-2.5 shadow-sm">
                  {m.content}
                </div>
              </div>
            );
            if (m.role === 'error') return (
              <div key={i} className="flex justify-start">
                <div className="max-w-[80%] bg-red-900/50 border border-red-800/50 text-red-300 text-xs rounded-2xl rounded-tl-sm px-3.5 py-2.5">
                  {m.content}
                </div>
              </div>
            );
            // Assistant response — clean card
            const d = m.data;
            return (
              <div key={i} className="flex justify-start">
                <div className="max-w-[90%] bg-slate-800 border border-slate-700/60 rounded-2xl rounded-tl-sm px-4 py-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                    <span className="text-sm font-semibold text-white">{d.report_name}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs px-2 py-0.5 bg-sky-500/20 text-sky-300 rounded-full border border-sky-500/30">
                      {d.row_count} rows
                    </span>
                    <span className="text-xs px-2 py-0.5 bg-slate-700 text-slate-300 rounded-full capitalize">
                      {d.data_source}
                    </span>
                    {d.report_description && (
                      <span className="text-xs text-slate-400 line-clamp-1">{d.report_description}</span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500">Results loaded in preview panel</p>
                </div>
              </div>
            );
          })}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-slate-800 border border-slate-700/60 rounded-2xl rounded-tl-sm px-4 py-3">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-sky-400" />
                  <span className="text-xs text-slate-400">Generating report...</span>
                </div>
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>
      </div>

      {/* Suggestions */}
      <div>
        <p className="text-xs text-slate-400 mb-2 font-medium">Quick prompts</p>
        <div className="flex flex-wrap gap-1.5">
          {SUGGESTIONS.map((s, i) => (
            <button
              key={i}
              onClick={() => !loading && handleQuery(s)}
              disabled={loading}
              className="text-xs px-2.5 py-1.5 bg-slate-50 text-slate-600 rounded-full 
                         hover:bg-sky-50 hover:text-sky-700 border border-slate-200 hover:border-sky-300 
                         transition-colors disabled:opacity-40"
              data-testid={`ai-suggestion-${i}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Input */}
      <div className="flex gap-2">
        <Input
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !loading && handleQuery()}
          placeholder='Ask AI to build any report…'
          className="flex-1"
          disabled={loading}
          data-testid="ai-query-input"
        />
        <Button
          onClick={() => handleQuery()}
          disabled={loading || !query.trim()}
          className="bg-sky-600 hover:bg-sky-700 shrink-0"
          data-testid="ai-send-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
}

// ── Saved Reports Panel ───────────────────────────────────
function SavedReports({ reports, subscribed, onLoad, onDelete }) {
  if (!subscribed) {
    return (
      <div className="text-center py-12 space-y-3" data-testid="subscription-gate">
        <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
          <AlertCircle className="w-6 h-6 text-amber-500" />
        </div>
        <p className="font-medium text-gray-700">Subscription Required</p>
        <p className="text-sm text-gray-500">An active subscription is required to save and load custom reports.</p>
      </div>
    );
  }
  if (reports.length === 0) {
    return (
      <div className="text-center py-12 text-gray-400" data-testid="no-saved-reports">
        <FolderOpen className="w-8 h-8 mx-auto mb-2 opacity-40" />
        <p className="text-sm">No saved reports yet. Run a report and click "Save Report".</p>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="saved-reports-list">
      {reports.map(r => (
        <div key={r.report_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-sky-50 hover:border-sky-200 transition-colors">
          <div className="flex-1 min-w-0">
            <p className="font-medium text-gray-800 text-sm truncate">{r.name}</p>
            <p className="text-xs text-gray-400 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {new Date(r.created_at).toLocaleDateString()}
              {r.description && <span> · {r.description}</span>}
            </p>
          </div>
          <div className="flex items-center gap-2 ml-3">
            <Button size="sm" variant="outline" onClick={() => onLoad(r)} data-testid={`load-report-${r.report_id}`}>
              <Play className="w-3.5 h-3.5 mr-1" /> Load
            </Button>
            <Button size="sm" variant="ghost" onClick={() => onDelete(r.report_id)} className="text-red-400 hover:bg-red-50 hover:text-red-600" data-testid={`delete-report-${r.report_id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Save Modal ─────────────────────────────────────────────
function SaveModal({ onSave, onDismiss }) {
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full mx-4 space-y-4" data-testid="save-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Save Report</h3>
          <button onClick={onDismiss} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-4 h-4" /></button>
        </div>
        <Input value={name} onChange={e => setName(e.target.value)} placeholder="Report name" autoFocus data-testid="save-name-input" />
        <Input value={desc} onChange={e => setDesc(e.target.value)} placeholder="Description (optional)" data-testid="save-desc-input" />
        <div className="flex gap-3">
          <Button variant="outline" onClick={onDismiss} className="flex-1">Cancel</Button>
          <Button onClick={() => onSave(name, desc)} disabled={!name.trim()} className="flex-1 bg-sky-600 hover:bg-sky-700 text-white" data-testid="save-confirm-btn">
            <Save className="w-4 h-4 mr-2" /> Save
          </Button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  MAIN PAGE
// ════════════════════════════════════════════════════════════

export default function CustomReportBuilderPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('templates'); // templates | builder | ai | saved
  const [templates, setTemplates] = useState([]);
  const [savedReports, setSavedReports] = useState([]);
  const [subscribed, setSubscribed] = useState(false);
  const [result, setResult] = useState(null);   // { rows, report_name, data_source, generated_config? }
  const [activeConfig, setActiveConfig] = useState(null);
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [builderInitConfig, setBuilderInitConfig] = useState(null);

  useEffect(() => {
    loadTemplates();
    loadSaved();
  }, []);

  const loadTemplates = async () => {
    try {
      const res = await fetch(`${API_URL}/api/custom-reports/templates`);
      const data = await res.json();
      setTemplates(data.templates || []);
    } catch { toast.error('Could not load templates'); }
  };

  const loadSaved = async () => {
    try {
      const res = await fetch(`${API_URL}/api/custom-reports/saved`);
      const data = await res.json();
      setSavedReports(data.reports || []);
      setSubscribed(data.subscribed !== false);
    } catch {}
  };

  const runReport = async (config) => {
    setRunning(true);
    setActiveConfig(config);
    try {
      const res = await fetch(`${API_URL}/api/custom-reports/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      });
      if (!res.ok) throw new Error('Report failed');
      const data = await res.json();
      setResult(data);
      toast.success(`Report generated: ${data.row_count} rows`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setRunning(false);
    }
  };

  const handleTemplateSelect = (tpl) => {
    const today = new Date().toISOString().slice(0, 10);
    const firstOfYear = `${new Date().getFullYear()}-01-01`;
    const config = {
      name: tpl.name,
      description: tpl.description,
      data_source: tpl.data_source,
      report_type: tpl.report_type,
      date_from: tpl.default_date_range === 'all' ? null : firstOfYear,
      date_to: tpl.default_date_range === 'all' ? null : today,
      groupBy: tpl.groupBy,
      sort_by: 'amount',
      sort_dir: 'desc',
      branding: { accent_color: '#0ea5e9' },
      template_id: tpl.id,
    };
    setBuilderInitConfig(config);
    setActiveTab('builder');
  };

  const handleAIResult = (data) => {
    setResult(data);
    if (data.generated_config) {
      setActiveConfig({
        name: data.report_name,
        description: data.report_description || '',
        ...data.generated_config,
        branding: { accent_color: '#0ea5e9' },
      });
    }
  };

  const handleSave = async (name, desc) => {
    if (!activeConfig) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/custom-reports/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, description: desc, config: activeConfig }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Save failed');
      }
      toast.success('Report saved');
      setShowSaveModal(false);
      await loadSaved();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (reportId) => {
    try {
      await fetch(`${API_URL}/api/custom-reports/saved/${reportId}`, { method: 'DELETE' });
      toast.success('Report deleted');
      await loadSaved();
    } catch { toast.error('Delete failed'); }
  };

  const handleLoadSaved = (r) => {
    if (r.config) {
      setBuilderInitConfig(r.config);
      setActiveTab('builder');
    }
  };

  const TABS = [
    { id: 'templates', label: 'Templates', icon: Layout },
    { id: 'builder', label: 'Visual Builder', icon: Filter },
    { id: 'ai', label: 'AI Query', icon: Sparkles },
    { id: 'saved', label: `Saved (${savedReports.length})`, icon: FolderOpen },
  ];

  // When templates tab: full-width; otherwise split
  const isTemplates = activeTab === 'templates';

  return (
    <div className="max-w-7xl mx-auto space-y-5 py-5" data-testid="custom-report-builder-page">

      {/* ── Hero Header with AI accent ── */}
      <div className="relative rounded-2xl overflow-hidden bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 p-5 md:p-6">
        {/* Subtle circuit dots */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle, #38bdf8 1px, transparent 1px)',
          backgroundSize: '28px 28px'
        }} />
        <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-sky-500/20 text-sky-300 border border-sky-500/30 tracking-wide uppercase">
                AI Powered
              </span>
              <span className="text-xs text-slate-500">Subscription Active</span>
            </div>
            <h1 className="text-xl md:text-2xl font-bold text-white tracking-tight">Custom Report Builder</h1>
            <p className="text-sm text-slate-400">Build, analyze, and export branded financial reports using templates, visual builder, or natural language</p>
          </div>
          {result && (
            <div className="shrink-0">
              <ExportBar
                config={activeConfig}
                rows={result?.rows}
                onSave={() => setShowSaveModal(true)}
                saving={saving}
              />
            </div>
          )}
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit" data-testid="report-tabs">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === t.id
                  ? t.id === 'ai'
                    ? 'bg-slate-800 text-white shadow-sm'
                    : 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid={`tab-${t.id}`}
            >
              <Icon className={`w-4 h-4 ${t.id === 'ai' && activeTab === 'ai' ? 'text-sky-400' : ''}`} />
              {t.label}
              {t.id === 'ai' && <span className="w-1.5 h-1.5 rounded-full bg-sky-400 animate-pulse ml-0.5" />}
            </button>
          );
        })}
      </div>

      {/* ── Full-width Templates tab ── */}
      {isTemplates && (
        <Card>
          <CardContent className="p-6">
            <TemplateGallery templates={templates} onSelect={handleTemplateSelect} />
          </CardContent>
        </Card>
      )}

      {/* ── Split panel for Builder / AI / Saved ── */}
      {!isTemplates && (
        <div className="grid grid-cols-1 xl:grid-cols-5 gap-5">
          {/* Left config panel */}
          <div className="xl:col-span-2">
            <Card>
              <CardContent className="p-5">
                {activeTab === 'builder' && (
                  <VisualBuilder
                    key={JSON.stringify(builderInitConfig)}
                    initialConfig={builderInitConfig}
                    onRun={runReport}
                    running={running}
                  />
                )}
                {activeTab === 'ai' && (
                  <AIQueryPanel onResult={handleAIResult} />
                )}
                {activeTab === 'saved' && (
                  <SavedReports
                    reports={savedReports}
                    subscribed={subscribed}
                    onLoad={handleLoadSaved}
                    onDelete={handleDelete}
                  />
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right preview panel */}
          <div className="xl:col-span-3">
            <Card className="h-full min-h-[400px]">
              <CardHeader className="pb-3 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-gray-400" />
                    {result ? result.report_name : 'Report Preview'}
                  </CardTitle>
                  {result && (
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-400" data-testid="row-count">{result.row_count} rows</span>
                      <span className="text-xs px-2 py-0.5 bg-sky-100 text-sky-700 rounded-full">{result.data_source}</span>
                      <Button size="sm" variant="ghost" onClick={() => setResult(null)} className="h-7 w-7 p-0">
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
                {result?.report_description && (
                  <p className="text-xs text-gray-400 mt-1">{result.report_description}</p>
                )}
              </CardHeader>
              <CardContent className="p-0">
                {running ? (
                  <div className="flex flex-col items-center justify-center py-20 space-y-3">
                    <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
                    <p className="text-sm text-gray-500">Running report...</p>
                  </div>
                ) : result ? (
                  <ReportTable rows={result.rows} reportName={result.report_name} />
                ) : (
                  <div className="flex flex-col items-center justify-center py-20 space-y-3 text-gray-300" data-testid="report-placeholder">
                    <BarChart3 className="w-16 h-16" />
                    <div className="text-center space-y-1">
                      <p className="text-sm font-medium text-gray-400">No report running yet</p>
                      <p className="text-xs text-gray-300">Configure your report on the left and click Run Report</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Save Modal */}
      {showSaveModal && (
        <SaveModal
          onSave={handleSave}
          onDismiss={() => setShowSaveModal(false)}
        />
      )}
    </div>
  );
}
